import ApiService from 'api-client/ApiService'
import config from 'app-config'
import { NovaServersResponse } from 'app/plugins/openstack/components/vms/model'
import {
  VirtualMachineLite,
  VirtualMachinesCountResponse,
  VMsListLiteResponse,
  VMsListResponse,
} from 'app/plugins/openstack/components/vms/newModel'
import { getDomainId } from './helpers'
import { convertVMFilterParamsToQueryParams } from 'app/plugins/openstack/components/vms/utils'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'

class Nova extends ApiService {
  public getClassName() {
    return 'nova'
  }

  /**
   * Returns the base URL for the Neutron API
   */
  get baseURL(): string {
    if (location.hostname === 'localhost') {
      return `${config.apiHost}/pf9`
    }

    return `${location.origin}/pf9`
  }

  protected async getEndpoint() {
    return this.client.keystone.getServiceEndpoint('nova', 'public')
  }

  flavorsUrl = () => `/flavors`
  instancesUrl = () => `/servers`
  hypervisorsUrl = () => `/os-hypervisors`
  sshKeysUrl = () => `/os-keypairs`
  CQRSVmsList = (tenant_id = '') => {
    const domainId = getDomainId()

    if (!domainId || !this.client.activeProjectId) {
      return ''
    }

    return `/vmslist?domain_id=${domainId}&tenant_id=${
      tenant_id ? tenant_id : this.client.activeProjectId
    }`
  }

  checkHealth = async () => {
    return this.client.basicGet<any>({
      url: `/`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'checkHealth',
      },
    })
  }

  getVersionDetails = async () => {
    const response = await this.client.basicGet<any>({
      url: `/`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getNovaVersion',
      },
    })
    return response
  }

  getFlavors = async () => {
    // const url = `${this.flavorsUrl()}/detail?is_public=no`
    const response = await this.client.basicGet<any>({
      url: `/flavors/detail?is_public=None`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackFlavors',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
            // 'X-OpenStack-Nova-API-Version': '2.61',
          },
        },
      },
    })
    return response.flavors
  }

  getFlavor = async (flavorId) => {
    const response = await this.client.basicGet<any>({
      url: `/flavors/${flavorId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackFlavor',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
            // 'X-OpenStack-Nova-API-Version': '2.61',
          },
        },
      },
    })
    return response.flavor
  }

  createFlavor = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/flavors`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createFlavor',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.flavor
  }

  createFlavorMetadata = async ({ id, body }) => {
    const response = await this.client.basicPost<any>({
      url: `/flavors/${id}/os-extra_specs`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createFlavorMetadata',
      },
    })
    return response
  }

  updateFlavorMetadata = async ({ id, body, key }) => {
    const response = await this.client.basicPut<any>({
      url: `/flavors/${id}/os-extra_specs/${key}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateFlavorMetadata',
      },
    })
    return response
  }

  deleteFlavorMetadata = async ({ id, key }) => {
    return this.client.basicDelete<any>({
      url: `/flavors/${id}/os-extra_specs/${key}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteFlavorMetadata',
      },
    })
  }

  getFlavorTenants = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/flavors/${id}/os-flavor-access`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getFlavorTenants',
      },
    })
    return response.flavor_access
  }

  addFlavorTenant = async ({ id, body }) => {
    const response = await this.client.basicPost<any>({
      url: `/flavors/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'addFlavorTenant',
      },
    })
    return response
  }

  removeFlavorTenant = async ({ id, body }) => {
    const response = await this.client.basicPost<any>({
      url: `/flavors/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'removeFlavorTenant',
      },
    })
    return response
  }

  deleteFlavor = async (id) => {
    return this.client.basicDelete<any>({
      url: `/flavors/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteFlavor',
      },
    })
  }

  getInstances = async () => {
    try {
      const response = await this.client.basicGet<VMsListResponse>({
        url: this.CQRSVmsList(),
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getOpenstackVms',
        },
      })
      return response.vms || []
    } catch (e) {
      return []
    }
  }

  async getVirtualMachinesCount(query_params = '', tenant_id = '') {
    try {
      let url = `${this.CQRSVmsList(tenant_id)}&count_only=true`
      if (query_params) {
        url += `&${query_params}`
      }

      const response = await this.client.basicGet<VirtualMachinesCountResponse>({
        url,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getVirtualMachinesCount',
        },
      })

      return (
        response?.server_counts || {
          ACTIVE: 0,
          BUILDING: 0,
          PAUSED: 0,
          STOPPED: 0,
          SUSPENDED: 0,
          TOTAL_COUNT: 0,
        }
      )
    } catch (e) {
      return {
        ACTIVE: 0,
        BUILDING: 0,
        PAUSED: 0,
        STOPPED: 0,
        SUSPENDED: 0,
        TOTAL_COUNT: 0,
      }
    }
  }

  // Get the list of all VM - only name, id
  getAllInstancesLite = async (
    queryParams = '',
    searchParams?: Record<string, string>,
    desiredFields?: string[],
  ): Promise<VirtualMachineLite[]> => {
    try {
      // Build query string
      let queryString = queryParams
      // Add search parameters
      if (searchParams) {
        queryString += convertVMFilterParamsToQueryParams(searchParams)
      }
      if (desiredFields?.length) {
        queryString += `&fields=${desiredFields.join(',')}`
      }

      const url = desiredFields?.length
        ? this.CQRSVmsList()
        : `${this.CQRSVmsList()}&names_only=true`
      const response = await this.client.basicGet<VMsListLiteResponse>({
        url: queryString ? `${url}&${queryString}` : url,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getAllInstancesLite',
        },
      })
      return response.vms || []
    } catch (e) {
      return []
    }
  }

  getAllInstances = async () => {
    try {
      const url = this.CQRSVmsList()
      const response = await this.client.basicGet<VMsListResponse>({
        url: `${url}&all_tenants=true`,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getAllOpenstackVms',
        },
      })
      return response.vms || []
    } catch (e) {
      return []
    }
  }

  getInstancesSince = async (timestamp) => {
    try {
      const response = await this.client.basicGet<VMsListResponse>({
        url: `${this.CQRSVmsList()}&changes-since=${timestamp}`,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getOpenstackVmsSince',
        },
      })
      return response.vms || []
    } catch (e) {
      return []
    }
  }

  getAllInstancesSince = async (timestamp) => {
    try {
      const response = await this.client.basicGet<VMsListResponse>({
        url: `${this.CQRSVmsList()}&all_tenants=true&changes-since=${timestamp}`,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getAllOpenstackVmsSince',
        },
      })
      return response.vms || []
    } catch (e) {
      return []
    }
  }

  getSelfInstancesSince = async (timestamp, userId) => {
    try {
      const response = await this.client.basicGet<VMsListResponse>({
        url: `${this.CQRSVmsList()}&owner=${userId}&changes-since=${timestamp}`,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getSelfOpenstackVmsSince',
        },
      })
      return response.vms || []
    } catch (e) {
      return []
    }
  }

  getInstancesForReservationId = async (reservationId) => {
    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail?reservation_id=${reservationId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackVmsWithReservationId',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  getInstancesForHost = async (hostId) => {
    try {
      const url = this.CQRSVmsList()
      const response = await this.client.basicGet<VMsListResponse>({
        url: `${url}&all_tenants=true&deleted=False&host=${hostId}`,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getHostOpenstackVms',
        },
      })
      return response.vms || []
    } catch (e) {
      return []
    }
  }

  getInstancesForUser = async (userId) => {
    try {
      const url = this.CQRSVmsList()
      const response = await this.client.basicGet<VMsListResponse>({
        url: `${url}&owner=${userId}`,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getUserOpenstackVms',
        },
      })
      return response.vms || []
    } catch (e) {
      return []
    }
  }

  getInstancesPaginated = async ({
    limit = 100,
    marker,
    sortKey = 'created_at',
    sortDir = 'desc',
    allTenants = false,
    userId,
    searchParams,
    changesSince,
  }: {
    limit?: number
    marker?: string
    sortKey?: string
    sortDir?: 'asc' | 'desc'
    allTenants?: boolean
    userId?: string
    searchParams?: Record<string, string>
    changesSince?: string
  }) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams()
      const tenantId = searchParams?.tenant_id

      if (limit) {
        queryParams.append('limit', limit.toString())
      }

      if (marker) {
        queryParams.append('marker', marker)
      }

      if (sortKey) {
        queryParams.append('sort_key', sortKey)
      }

      if (sortDir) {
        queryParams.append('sort_dir', sortDir)
      }

      // only add all_tenants if tenantId is not provided
      if (allTenants && !tenantId) {
        queryParams.append('all_tenants', 'True')
      }

      if (userId) {
        queryParams.append('owner', userId)
      }

      if (changesSince) {
        queryParams.append('changes-since', changesSince)
      }

      const response = await this.client.basicGet<VMsListResponse>({
        url: `${this.CQRSVmsList(
          tenantId,
        )}&${queryParams.toString()}${convertVMFilterParamsToQueryParams(searchParams)}`,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getInstancesPaginated',
        },
      })
      return response.vms || []
    } catch (e) {
      return []
    }
  }

  searchInstanceAcrossTenants = async ({ query, property }) => {
    try {
      const url = this.CQRSVmsList()
      const response = await this.client.basicGet<VMsListResponse>({
        url: `${url}&all_tenants=true&${property}=${query}`,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'searchAllOpenstackVms',
        },
      })
      return response.vms || []
    } catch (e) {
      return []
    }
  }

  getInstancesWithTag = async (tag) => {
    try {
      const url = this.CQRSVmsList()
      const response = await this.client.basicGet<VMsListResponse>({
        url: `${url}&all_tenants=true&tags=${tag}`,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getTagOpenstackVms',
        },
      })
      return response.vms || []
    } catch (e) {
      return []
    }
  }

  getInstance = async (instanceId: string) => {
    const userIsAdmin = isAdmin()
    try {
      const url = this.CQRSVmsList()
      const response = await this.client.basicGet<any>({
        url: userIsAdmin
          ? `${url}&all_tenants=true&uuid=${instanceId}`
          : `${url}&uuid=${instanceId}`,
        endpoint: this.baseURL,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getOpenstackVm',
        },
      })
      return Array.isArray(response?.vms) ? response.vms[0] : []
    } catch (e) {
      return []
    }
  }

  createInstance = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/servers`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createOpenstackVm',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.server
  }

  createInstances = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/servers`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createOpenstackVms',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response
  }

  getInstaceDetails = async (id) => {
    const url = `/servers/${id}`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getInstaceDetail',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.server
  }

  updateInstance = async (id, body) => {
    const projectId = this.client.activeProjectId
    const url = `/servers/${id}`
    const response = await this.client.basicPut<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateOpenstackVm',
      },
    })
    return response.server
  }

  setInstanceMetadata = async ({ id, body }) => {
    const response = await this.client.basicPut<any>({
      url: `/servers/${id}/metadata`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateOpenstackVmMetadata',
      },
    })
    return response.metadata
  }

  addInstanceTag = async ({ id, tag }) => {
    const response = await this.client.basicPut<any>({
      url: `/servers/${id}/tags/${tag}`,
      body: null,
      options: {
        clsName: this.getClassName(),
        mthdName: 'addOpenstackVmTag',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response
  }

  deleteInstance = async (id) => {
    return this.client.basicDelete({
      url: `/servers/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteOpenstackVm',
      },
    })
  }

  async getServerGroups() {
    const response = await this.client.basicGet<any>({
      url: `/os-server-groups`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getServerGroups',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.server_groups
  }

  async getAllServerGroups() {
    const response = await this.client.basicGet<any>({
      url: `/os-server-groups?all_projects=true`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAllServerGroups',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.server_groups
  }

  createServerGroup = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/os-server-groups`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createServerGroup',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.server_group
  }

  deleteServerGroup = async (id) => {
    return this.client.basicDelete({
      url: `/os-server-groups/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteServerGroup',
      },
    })
  }

  async getHypervisors() {
    // const url = `${this.hypervisorsUrl()}/detail`
    const response = await this.client.basicGet<any>({
      url: `/os-hypervisors/detail`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHypervisors',
      },
    })
    return response.hypervisors
  }

  getHypervisor = async (id) => {
    const response = await this.client.basicGet<any>({
      url: `/os-hypervisors/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHypervisor',
      },
    })
    return response.hypervisor
  }

  enableHypervisorScheduling = async (body) => {
    const response = await this.client.basicPut<any>({
      url: '/os-services/enable',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'enableHypervisorScheduling',
      },
    })
    return response
  }

  disableHypervisorScheduling = async (body) => {
    const response = await this.client.basicPut<any>({
      url: '/os-services/disable',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'disableHypervisorScheduling',
      },
    })
    return response
  }

  enableMaintenanceMode = async (body) => {
    const response = await this.client.basicPut<any>({
      url: '/os-services/disable-log-reason',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'enableMaintenanceMode',
      },
    })
    return response
  }

  async getHostAggregates() {
    const response = await this.client.basicGet<any>({
      url: `/os-aggregates`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHostAggregates',
      },
    })
    return response.aggregates
  }

  createHostAggregate = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/os-aggregates`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createHostAggregate',
      },
    })
    return response.aggregate
  }

  getHostAggregate = async (id) => {
    const response = await this.client.basicGet<any>({
      url: `/os-aggregates/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHostAggregate',
      },
    })
    return response.aggregate
  }

  updateHostAggregate = async ({ id, body }) => {
    const response = await this.client.basicPut<any>({
      url: `/os-aggregates/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateHostAggregate',
      },
    })
    return response.aggregate
  }

  hostAggregateAction = async ({ id, body }) => {
    const response = await this.client.basicPost<any>({
      url: `/os-aggregates/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'hostAggregateAction',
      },
    })
    return response.aggregate
  }

  deleteHostAggregate = async (id) => {
    return this.client.basicDelete<any>({
      url: `/os-aggregates/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteHostAggregate',
      },
    })
  }

  async getAvailabilityZones() {
    const response = await this.client.basicGet<any>({
      url: `/os-availability-zone/detail`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAvailabilityZones',
      },
    })
    return response.availabilityZoneInfo
  }

  async getSshKeys() {
    const response = await this.client.basicGet<any>({
      url: `/os-keypairs`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSshKeys',
      },
    })
    return response.keypairs
  }

  async getSshKey(name) {
    const response = await this.client.basicGet<any>({
      url: `/os-keypairs/${name}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSshKey',
      },
    })
    return response.keypair
  }

  async getSshKeysForUser({ userId }) {
    const response = await this.client.basicGet<any>({
      url: `/os-keypairs?owner=${userId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSshKeysForUser',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.keypairs
  }

  createSshKey = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/os-keypairs`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createSshKeys',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.keypair
  }

  createSshKeyForUser = async ({ body }) => {
    const response = await this.client.basicPost<any>({
      url: `/os-keypairs`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createSshKeyForUser',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.keypair
  }

  async deleteSshKey(name) {
    return this.client.basicDelete<any>({
      url: `/os-keypairs/${name}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteSshKey',
      },
    })
  }

  async deleteSshKeyForUser({ name, userId }) {
    return this.client.basicDelete<any>({
      url: `/os-keypairs/${name}?owner=${userId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteSshKeyForUser',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
  }

  instanceAction = async ({ id, body }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'openstackInstanceAction',
      },
    })
  }

  instanceActionWithSpecificVersion = async ({ id, body }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'instanceActionWithVersion',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
  }

  getVolumeAttachments = async ({ vmId }) => {
    const attachments = await this.client.basicGet<any>({
      url: `/servers/${vmId}/os-volume_attachments`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getVolumeAttachments',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return attachments?.volumeAttachments || []
  }

  attachVolume = async ({ vmId, body }) => {
    return this.client.basicPost<any>({
      url: `/servers/${vmId}/os-volume_attachments`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'attachVolumeToInstance',
      },
    })
  }

  detachVolume = async ({ vmId, volumeId }) => {
    return this.client.basicDelete<any>({
      url: `/servers/${vmId}/os-volume_attachments/${volumeId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'detachVolumeFromInstance',
      },
    })
  }

  updateVolumeAttachment = async ({ id, volumeId, body }) => {
    return this.client.basicPut<any>({
      url: `/servers/${id}/os-volume_attachments/${volumeId}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateVolumeAttachment',
        config: {
          headers: {
            // Passed subversion here, since delete on termination is not supported to default 2.1 version.
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
  }

  createConsole = async ({ vmId }) => {
    // Try microversion 2.95 and use remote_consoles endpoint
    return this.client.basicPost<any>({
      url: `/servers/${vmId}/remote-consoles`,
      // url: `/servers/${vmId}/action`,
      // body: {
      //   'os-getVNCConsole': {
      //     type: 'novnc',
      //   },
      // },
      body: {
        remote_console: {
          protocol: 'vnc',
          type: 'novnc',
        },
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'createVmConsole',
        config: {
          headers: {
            // 'OpenStack-API-Version': 'compute 2.87', // sbux-scaletest
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
  }

  addSecurityGroupAction = async ({ id, securityGroupName }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body: {
        addSecurityGroup: {
          name: securityGroupName,
        },
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'openstackInstanceAction',
      },
    })
  }

  removeSecurityGroupAction = async ({ id, securityGroupName }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body: {
        removeSecurityGroup: {
          name: securityGroupName,
        },
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'openstackInstanceAction',
      },
    })
  }

  rescueVmAction = async ({ id, body }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'rescueVmAction',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
  }

  unrescueVmAction = async ({ id }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body: {
        unrescue: null,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'unrescueVmAction',
      },
    })
  }

  createInterface = async ({ id, body }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/os-interface`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createVmInterface',
      },
    })
  }

  detachInterface = async ({ id, portId }) => {
    return this.client.basicDelete<any>({
      url: `/servers/${id}/os-interface/${portId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'detachInterfaceFromVm',
      },
    })
  }

  getQuotas = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/os-quota-sets/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getNovaQuotas',
      },
    })
    return response.quota_set
  }

  updateQuotas = async ({ id, body }) => {
    const response = await this.client.basicPut<any>({
      url: `/os-quota-sets/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateNovaQuotas',
      },
    })
    return response.quota_set
  }

  getQuotaUsage = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/os-quota-sets/${id}/detail`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getQuotaUsage',
      },
    })
    return response.quota_set
  }

  getQuotasForUser = async ({ id, userId }) => {
    const response = await this.client.basicGet<any>({
      url: `/os-quota-sets/${id}/detail?owner=${userId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getNovaQuotasForUser',
      },
    })
    return response.quota_set
  }

  getSimpleTenantUsage = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/os-simple-tenant-usage/${id}?detailed=1`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSimpleTenantUsage',
      },
    })
    return response.tenant_usage
  }

  updateQuotasForUser = async ({ id, userId, body }) => {
    const response = await this.client.basicPut<any>({
      url: `/os-quota-sets/${id}?owner=${userId}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateNovaQuotasForUser',
      },
    })
    return response.quota_set
  }

  getVmEvents = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/servers/${id}/os-instance-actions`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getVmEvents',
      },
    })
    return response.instanceActions
  }

  getVmEventDetails = async ({ vmId, requestId }) => {
    const response = await this.client.basicGet<any>({
      url: `/servers/${vmId}/os-instance-actions/${requestId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getVmEventDetails',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.instanceAction
  }

  async getComputeServiceDetails() {
    const response = await this.client.basicGet<any>({
      url: `/os-services`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getComputeServiceDetails',
      },
    })
    return response.services
  }

  updateVirtualMachineOwner = async ({ id, body }) => {
    return await this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateVirtualMachineOwner',
      },
    })
  }

  getMigrations = async () => {
    const response = await this.client.basicGet<any>({
      url: `/os-migrations`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getMigrations',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.migrations
  }

  getMigrationsSince = async (timestamp) => {
    const response = await this.client.basicGet<any>({
      url: `/os-migrations?changes-since=${timestamp}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getMigrationsSince',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.migrations
  }
}

export default Nova
